/**
 * Who is signed in (PLAN §18, §2).
 *
 * The one runtime dependency, and it is taken for this and nothing else.
 * Token refresh, session persistence and the refresh-token race are fiddly and
 * security-sensitive: a refresh that two concurrent requests both attempt can
 * spend the token twice and log somebody out mid-campaign. Data access is a
 * URL, a header and a JSON body, and gets none of this — see `data.ts`.
 *
 * **Signups are disabled in the project, and administrators are invited.** That
 * is not enforced here — it cannot be, since the sign-up endpoint is GoTrue's
 * and the anon key reaches it directly — which is why `test/db/rls.test.ts`
 * asserts it against the real project. Forgetting that one checkbox exposes
 * every campaign ever sent.
 */

import { createClient, type Session, type SupabaseClient } from '@supabase/supabase-js';
import { SUPABASE_ANON_KEY, SUPABASE_URL } from './config.ts';

export interface SignedIn {
  readonly email: string;
  readonly token: string;
}

let client: SupabaseClient | undefined;

function supabase(): SupabaseClient {
  client ??= createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // The panel has no OAuth flows, so there is no callback fragment to read
      // and nothing to gain from watching the URL.
      detectSessionInUrl: false,
    },
  });
  return client;
}

/** The current session, or nothing. Does not go to the network. */
export async function currentSession(): Promise<SignedIn | undefined> {
  const { data } = await supabase().auth.getSession();
  return asSignedIn(data.session);
}

/**
 * A fresh access token, refreshed if it is about to expire.
 *
 * Every request asks for this rather than holding one: a token captured when
 * the page loaded is a token that expires while somebody is reading a list of
 * ten thousand recipients.
 */
export async function accessToken(): Promise<string | undefined> {
  const { data } = await supabase().auth.getSession();
  return data.session?.access_token;
}

export interface SignInFailure {
  /** A catalogue key, never a sentence — GoTrue's own messages are English. */
  readonly messageKey: string;
}

export async function signIn(email: string, password: string): Promise<SignedIn | SignInFailure> {
  const { data, error } = await supabase().auth.signInWithPassword({ email, password });

  if (error !== null) {
    // Deliberately one message for every rejection. "No such user" and "wrong
    // password" told apart is a way to find out who has an account here.
    return { messageKey: 'login.error.credentials' };
  }

  const session = asSignedIn(data.session);
  return session ?? { messageKey: 'login.error.credentials' };
}

export async function signOut(): Promise<void> {
  await supabase().auth.signOut();
}

/** Called when the session ends for any reason — expiry, sign-out, another tab. */
export function onSessionChange(handler: (session: SignedIn | undefined) => void): void {
  supabase().auth.onAuthStateChange((_event, session) => handler(asSignedIn(session)));
}

function asSignedIn(session: Session | null): SignedIn | undefined {
  if (session === null) return undefined;
  return { email: session.user.email ?? '', token: session.access_token };
}
